import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import className from 'classnames';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';
import { LocationUtils, TrackingUtils } from '../../utils';
import {
    LocationProp,
    LegType,
    ProductManagerErrorProp,
    EntryPoint,
} from '../../models';
import { ErrorUtils } from '../../utils/ErrorUtils';
import { sendTrackingEvent } from '../../utils/OdigeoInteractions';
import HotelSelectorDropdown from './HotelSelectorDropdown';
import HotelSelectorInputDesktop from './HotelSelectorInputDesktop';
import { DestinationError } from './DestinationError';

export class HotelSelectorDesktop extends PureComponent {
    state = {
        displayedWord: LocationUtils.getInputText(this.props.value, EntryPoint.HOTELS),
    };

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClick);

        let count = 0;
        let interval;
        interval = setInterval(() => {
            if (count >= TrackingUtils.MAX_ATTEMPTS) {
                sendTrackingEvent({
                    category: TrackingUtils.CATEGORY.HOME,
                    action: TrackingUtils.ACTION.HOTELS,
                    label: TrackingUtils.LABEL.fieldRendered(EntryPoint.HOTELS, 'searchbox')
                });
                clearInterval(interval);
            }
            count++;
        }, TrackingUtils.ATTEMPT_TIMEOUT);
    }

    componentDidUpdate(prevProps) {
        const { value } = this.props;
        const { displayedWord } = this.state;
        if (!isEqual(prevProps.value, value)) {
            if (value) {
                this.setState({ displayedWord: LocationUtils.getInputText(value, EntryPoint.HOTELS) });
            } else if (displayedWord !== '') {
                const isSwapOriginDestination =
                    displayedWord === (prevProps.value.displayName || prevProps.value.city);
                if (isSwapOriginDestination) {
                    this.setState({
                        displayedWord: '',
                    });
                }
            }
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClick);
    }

    getValidationErrorKey() {
        const { validationError } = this.props;
        return ErrorUtils.getLocationErrorKey(validationError, EntryPoint.HOTELS);
    }

    handleClick = (e) => {
        const { focus } = this.props;
        if (!this.node.contains(e.target) && focus) {
            this.handleClickOutside();
        }
    };

    onRemoveFocus = () => {
        const { fetchedLocations, value } = this.props;
        const { displayedWord } = this.state;
        const suggestionValue = LocationUtils.getFirstSuggestedLocation({
            fetchedLocations,
            displayedWord,
            departureOrArrival: LegType.ARRIVAL,
            activeProduct: EntryPoint.HOTELS,
        });
        if (!value && !suggestionValue) {
            this.setState({ displayedWord: '' });
        }
    };

    handleClickOutside = () => {
        const {
            fetchedLocations,
            onChangeFocus,
            value,
        } = this.props;
        const { displayedWord } = this.state;
        if (!value) {
            const suggestionValue = LocationUtils.getFirstSuggestedLocation({
                fetchedLocations,
                displayedWord,
                departureOrArrival: LegType.ARRIVAL,
                activeProduct: EntryPoint.HOTELS,
            });
            this.setNewValue(suggestionValue);
            if (!suggestionValue) {
                this.setState({ displayedWord: '' });
            }
        }
        onChangeFocus('', true); // Close dropdown location list on click outside the field
    };

    onChangeInput = (text) => {
        this.setState({ displayedWord: text }, () => this.setNewValue(null));
    };

    onClickInput = () => {
        const { onChangeFocus } = this.props;
        onChangeFocus(LegType.ARRIVAL, true);
    };

    onClearInput = () => {
        const { setValue } = this.props;
        setValue(null);
        this.setState({ displayedWord: '' });
    };

    onClickItem = (newValue) => {
        const { onChangeFocus } = this.props;
        onChangeFocus(LegType.ARRIVAL, false);
        this.setNewValue(newValue);
    };

    onClickItemSilent = () => {
        const { onChangeFocus } = this.props;
        onChangeFocus(LegType.ARRIVAL, false);
    };

    setNewValue = (newValue) => {
        const { value, setValue } = this.props;
        if (newValue !== value) {
            setValue(newValue);
        }
    };

    render() {
        const { validationError, focus } = this.props;
        const { displayedWord } = this.state;
        const hasError = !!validationError;
        const testId = 'airport-destination';
        const classNames = className({
            'invalid odf-input-inline-invalid': hasError,
        });
        const classNamesInput = className({
            'odf-input-group': true,
            'focus odf-input-inline-focus': focus,
        });
        const clearClassNames = className({
            'odf-input-icon odf-icon': true,
            'odf-icon-arrow-up odf-z-index-3': displayedWord,
            'odf-icon-cross-circle-light': displayedWord,
        });
        return (
            <div
                className={classNames}
                ref={(node) => (this.node = node)}
                test-id={testId}
                data-testid={testId}
            >
                <div className={classNamesInput}>
                    <HotelSelectorInputDesktop
                        displayedWord={displayedWord}
                        onClickInput={this.onClickInput}
                        onChangeInput={this.onChangeInput}
                        onRemoveFocus={this.onRemoveFocus}
                        focus={focus}
                        hasError={hasError}
                    />
                    <span className={clearClassNames} onClick={this.onClearInput} />

                    <HotelSelectorDropdown
                        displayedWord={displayedWord}
                        onClickItem={this.onClickItem}
                        onClickItemSilent={this.onClickItemSilent}
                        focus={focus}
                    />
                </div>
                {hasError && (<DestinationError validationErrorKey={this.getValidationErrorKey()} />)}
            </div>
        );
    }
}

HotelSelectorDesktop.propTypes = {
    validationError: ProductManagerErrorProp,
    setValue: PropTypes.func.isRequired,
    onChangeFocus: PropTypes.func.isRequired,
    focus: PropTypes.bool,
    value: LocationProp,
    fetchedLocations: PropTypes.object,
};

HotelSelectorDesktop.defaultProps = {
    value: null,
    focus: false,
    validationError: null,
    fetchedLocations: {},
};

const mapStateToProps = ({ locationAutocomplete }) => ({
    fetchedLocations: locationAutocomplete,
});

const connected = connect(mapStateToProps, {})(HotelSelectorDesktop);

export { connected as ConnectedHotelSelectorDesktop };
